/**
 * Supabase browser client — used ONLY for Realtime subscriptions.
 * All data mutations still go through the backend REST API.
 *
 * The client is dynamic: after login the AuthContext fetches
 * GET /api/realtime-config and calls reinitSupabase() to swap to the
 * tenant's actual Supabase project (instead of the static Dev env-var).
 * Only the non-secret project URL + anon key are persisted to localStorage
 * so page reloads start with the correct project immediately, without
 * waiting for a network round-trip. The realtime auth TOKEN is never
 * persisted to any Storage API (QA WEB-003 residual fix, 2026-09-26) — it
 * lives only in memory and is re-fetched by AuthContext on every mount.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js'

export const REALTIME_DISABLED =
  typeof process !== 'undefined' && process.env.NEXT_PUBLIC_DISABLE_REALTIME === '1'

/**
 * Realtime config — #811 round 3 (calendar real-time regression).
 *
 * Round 2 nuked auto-reconnect entirely (`Number.MAX_SAFE_INTEGER`) to dodge
 * the 41 s loop bug. Side effect Tony surfaced: once the WS drops (Cloudflare
 * idle-timeouts it every few minutes), the calendar tab in another window
 * NEVER recovers and looks frozen until F5.
 *
 * Round 3 — bounded reconnect: try a few times with growing gaps, then give
 * up. Caps total reconnect attempts to 5 over ~6 minutes so we cannot loop.
 * Pair this with the `refetchInterval`s on calendar/scheduled-event queries
 * — together they guarantee bounded staleness regardless of WS state.
 *
 * tries → delay (ms): 1→2s, 2→5s, 3→15s, 4→60s, 5→300s, ≥6→never (silent).
 *
 * Env kill-switch (`NEXT_PUBLIC_DISABLE_REALTIME=1`) still skips realtime
 * entirely if a future incident requires it.
 */
const RECONNECT_DELAYS = [2_000, 5_000, 15_000, 60_000, 300_000]
function reconnectAfterMs(tries: number): number {
  return RECONNECT_DELAYS[tries - 1] ?? Number.MAX_SAFE_INTEGER
}

const REALTIME_CONFIG_KEY = 'jm_realtime_config'

/**
 * Persisted shape only — QA WEB-003 residual fix (2026-09-26). The realtime
 * auth token itself is NEVER written to localStorage/sessionStorage; only the
 * project URL + anon key (both public/anon-scoped, safe to cache) live here
 * for fast page-load startup. The token lives only inside the supabase-js
 * client's own runtime state via `_client.realtime.setAuth()` — never in a
 * module variable or any Storage API.
 */
type PersistedRealtimeConfig = { supabaseUrl: string; supabaseAnonKey: string }

function loadSavedConfig(): PersistedRealtimeConfig | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(REALTIME_CONFIG_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedRealtimeConfig & { realtimeToken?: string }
    if (!parsed || typeof parsed !== 'object') return null
    // One-time migration cleanup: a pre-fix session may have already written a
    // realtimeToken into this key. Strip it and rewrite the key clean so the
    // secret doesn't keep sitting in localStorage.
    if ('realtimeToken' in parsed) {
      const cleaned: PersistedRealtimeConfig = {
        supabaseUrl: parsed.supabaseUrl,
        supabaseAnonKey: parsed.supabaseAnonKey,
      }
      localStorage.setItem(REALTIME_CONFIG_KEY, JSON.stringify(cleaned))
      return cleaned
    }
    return parsed
  } catch {
    return null
  }
}

function makeClient(url: string, key: string): SupabaseClient {
  return createClient(url, key, {
    realtime: {
      params: { eventsPerSecond: 2 },
      heartbeatIntervalMs: 25_000,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore — supabase-js passes this through to RealtimeClient
      reconnectAfterMs,
    },
  })
}

// ── Dynamic inner client ─────────────────────────────────────────────────────
// Start from saved config (correct project on page reload) or fall back to
// the static env-var Dev project (correct only on the Dev tenant).
const saved = loadSavedConfig()
let _currentUrl = saved?.supabaseUrl ?? process.env.NEXT_PUBLIC_SUPABASE_URL!
let _client: SupabaseClient = makeClient(
  _currentUrl,
  saved?.supabaseAnonKey ?? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
)
// No persisted realtime token to fast-start with — it is never stored (see
// PersistedRealtimeConfig above). AuthContext's initRealtimeClient() call on
// mount fetches a fresh token from the backend and calls reinitSupabase()
// shortly after, which live-applies it via setAuth() below.

/**
 * Swap the realtime client to the tenant's own Supabase project.
 * Called by AuthContext after login (and after session restore when config
 * has changed). Safe to call while subscriptions are open — removeAllChannels
 * tears them down cleanly before the new client takes over.
 */
export function reinitSupabase(url: string, key: string, realtimeToken?: string): void {
  // Fable5: SAME project, only the token changed (session refresh / restore). Update
  // auth on the EXISTING client and keep all open channels alive — do NOT tear down.
  // The old code rebuilt the client whenever a token was passed, which killed realtime
  // for the whole session: subscription hooks hold channels on the old client and never
  // re-subscribe (page-reload race + the 50-min refresh both hit this). Matches the
  // documented design in docs/REALTIME.md ("refresh calls setAuth, no channel teardown").
  if (url === _currentUrl) {
    if (realtimeToken) {
      _client.realtime.setAuth(realtimeToken)
      if (typeof window !== 'undefined') {
        // Persist only the non-secret project URL/key — the token stays in memory only.
        const cfg: PersistedRealtimeConfig = { supabaseUrl: url, supabaseAnonKey: key }
        localStorage.setItem(REALTIME_CONFIG_KEY, JSON.stringify(cfg))
      }
    }
    return
  }

  // Genuine project swap (different tenant DB) — tear down + rebuild.
  _client.removeAllChannels()
  _currentUrl = url
  _client = makeClient(url, key)
  if (realtimeToken) {
    _client.realtime.setAuth(realtimeToken)
  }

  if (typeof window !== 'undefined') {
    // Persist only the non-secret project URL/key — the token stays in memory only.
    const cfg: PersistedRealtimeConfig = { supabaseUrl: url, supabaseAnonKey: key }
    localStorage.setItem(REALTIME_CONFIG_KEY, JSON.stringify(cfg))
  }
}

/** Remove the saved realtime config — called on logout so the next login
 *  starts fresh (avoids serving a stale tenant's project URL to a new user). */
export function clearRealtimeConfig(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(REALTIME_CONFIG_KEY)
  }
  // Reset to env-var defaults so Dev-only sessions still work. Rebuilding via
  // makeClient() (no setAuth() call) drops any live realtime token — there is
  // no separate in-memory token variable to clear; the only place the token
  // ever lived was inside the old _client's own runtime state, and that
  // client is discarded here.
  _client.removeAllChannels()
  _client = makeClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
  )
}

// ── Stable proxy export ───────────────────────────────────────────────────────
// All hooks import `supabase` and call methods on it. The Proxy delegates
// every property access and method call to the current `_client`, so when
// reinitSupabase() swaps _client, all callers transparently use the new one.
export const supabase = new Proxy({} as SupabaseClient, {
  get(_: SupabaseClient, prop: string | symbol) {
    const val = (_client as any)[prop as string]
    return typeof val === 'function' ? (val as Function).bind(_client) : val
  },
})
